import * as React from 'react';

import {
  enumPurchaseLoadStatus,
  type LoadStatus,
  type PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { stringToHue } from '@rematter/ui-web';

import {
  type ButtonConfig,
  BUTTON_COUNT,
  getLayout,
  renderActionButton,
  renderDigitButton,
  renderMaterialButton,
  renderPaginationButton,
  renderWideButton,
} from '~/shared/lib/ustudio';
import { useUStudio } from '~/shared/providers/ustudio/useUStudio';

type ScreenState = 'MaterialSelect' | 'LineItemEdit' | 'UnitSelect';

type QuickAddItem = {
  id: string;
  mnemonic?: string | null;
  itemGroupId?: string | null;
  effectivePricingUnit?: string;
};

type UnitOption = {
  value: string;
  label: string;
};

type UStudioScaleTicketControllerProps = {
  onAddItem: (item: QuickAddItem) => void;
  onAddDeduction: () => void;
  onFocusField: (
    lineItemId: string,
    field: 'gross' | 'tare' | 'unitPrice' | 'unit' | 'material',
  ) => void;
  onSetFieldValue: (lineItemId: string, field: string, value: string) => void;
  onSetUnit: (lineItemId: string, unit: string) => void;
  onHold: () => void;
  onRelease: () => void;
  onFinish: () => void;
  onTakeWeight: (lineItemId: string, field: 'gross' | 'tare') => void;
  quickAddItems: QuickAddItem[];
  availableUnits: UnitOption[];
  ticketStatus: PurchaseLoadStatus | LoadStatus;
  isHeld: boolean;
  currentLineItemId: string | null;
};

const MATERIAL_SLOTS = [0, 1, 2, 5, 6, 7, 10, 11, 12] as const;
const MATERIALS_PER_PAGE = MATERIAL_SLOTS.length;

const DIGIT_MAP: Record<number, string> = {
  0: '1',
  1: '2',
  2: '3',
  5: '4',
  6: '5',
  7: '6',
  10: '7',
  11: '8',
  12: '9',
};

function getFinishLabel(status: PurchaseLoadStatus | LoadStatus): string {
  switch (status) {
    case enumPurchaseLoadStatus.InventoryComplete:
      return 'Complete Pricing';
    case enumPurchaseLoadStatus.PriceComplete:
      return 'Create Bill & Pay';
    default:
      return 'Finish';
  }
}

function getFinishColor(status: PurchaseLoadStatus | LoadStatus): string {
  if (status === enumPurchaseLoadStatus.PriceComplete) return '#16a34a';
  return '#2563eb';
}

async function renderFinishButton(
  status: PurchaseLoadStatus | LoadStatus,
  width: number,
  height: number,
): Promise<ArrayBuffer> {
  return renderWideButton(
    getFinishLabel(status),
    getFinishColor(status),
    '#fff',
    width,
    height,
  );
}

async function restoreInitialLayout(agent: {
  fillButton: (i: number, r: number, g: number, b: number) => Promise<void>;
  drawButton: (i: number, data: ArrayBuffer) => Promise<void>;
}): Promise<void> {
  const layout = getLayout();
  const btn13 = layout.buttons[13];

  for (let i = 0; i < BUTTON_COUNT; i++) {
    if (i !== 13) {
      await agent.fillButton(i, 0, 0, 0);
    }
  }

  try {
    const response = await fetch('/icon.png');
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);

    const canvas = new OffscreenCanvas(btn13.width, btn13.height);
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, btn13.width, btn13.height);

    const scale = Math.min(
      btn13.width / bitmap.width,
      btn13.height / bitmap.height,
    );
    const drawW = Math.round(bitmap.width * scale);
    const drawH = Math.round(bitmap.height * scale);
    const drawX = Math.round((btn13.width - drawW) / 2);
    const drawY = Math.round((btn13.height - drawH) / 2);
    ctx.drawImage(bitmap, drawX, drawY, drawW, drawH);
    bitmap.close();

    const pngBlob = await canvas.convertToBlob({ type: 'image/png' });
    const pngData = await pngBlob.arrayBuffer();
    await agent.drawButton(13, pngData);
  } catch {
    await agent.fillButton(13, 0, 0, 0);
  }
}

//
// ⚛️ ⚛️ ⚛️ USTUDIO SCALE TICKET CONTROLLER ⚛️ ⚛️ ⚛️
//
export const UStudioScaleTicketController = ({
  onAddItem,
  onAddDeduction,
  onFocusField,
  onSetFieldValue,
  onSetUnit,
  onHold,
  onRelease,
  onFinish,
  onTakeWeight,
  quickAddItems,
  availableUnits,
  ticketStatus,
  isHeld,
  currentLineItemId,
}: UStudioScaleTicketControllerProps) => {
  const { agent } = useUStudio();

  const [screen, setScreen] = React.useState<ScreenState>('MaterialSelect');
  const [materialPage, setMaterialPage] = React.useState(0);
  const [weightField, setWeightField] = React.useState<'gross' | 'tare'>(
    'gross',
  );
  const centsRef = React.useRef(0);
  const focusedFieldRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!currentLineItemId) {
      setScreen('MaterialSelect');
      centsRef.current = 0;
      focusedFieldRef.current = null;
    }
  }, [currentLineItemId]);

  React.useEffect(() => {
    if (!agent) {
      console.debug('[UStudio] no agent, skipping button render');
      return;
    }

    const layout = getLayout();
    const btn13 = layout.buttons[13];

    if (screen === 'MaterialSelect') {
      const totalPages = Math.ceil(quickAddItems.length / MATERIALS_PER_PAGE);
      const pageItems = quickAddItems.slice(
        materialPage * MATERIALS_PER_PAGE,
        (materialPage + 1) * MATERIALS_PER_PAGE,
      );

      const materialImagePromises = pageItems.map(async (item, idx) => {
        const btnIndex = MATERIAL_SLOTS[idx];
        const hue = stringToHue(item.itemGroupId ?? '');
        const image = await renderMaterialButton(item.mnemonic ?? '?', hue);
        return {
          index: btnIndex,
          image,
          onPress: () => {
            onAddItem(item);
            setScreen('LineItemEdit');
            centsRef.current = 0;
            focusedFieldRef.current = 'gross';
          },
        } satisfies ButtonConfig;
      });

      const emptySlots = MATERIAL_SLOTS.slice(pageItems.length);
      const clearEmpty = async () => {
        for (const btnIndex of emptySlots) {
          await agent.fillButton(btnIndex, 0, 0, 0);
        }
      };

      const deductionPromise = renderActionButton('D', '#374151', '#666').then(
        (image) => ({
          index: 3,
          image,
        }),
      );

      const paginationPromises: Promise<ButtonConfig>[] = [];
      if (totalPages > 1) {
        paginationPromises.push(
          renderPaginationButton('next').then((image) => ({
            index: 4,
            image,
            onPress: () =>
              setMaterialPage((p) => (p + 1 >= totalPages ? 0 : p + 1)),
          })),
          renderPaginationButton('prev').then((image) => ({
            index: 9,
            image,
            onPress: () =>
              setMaterialPage((p) => (p - 1 < 0 ? totalPages - 1 : p - 1)),
          })),
        );
      } else {
        paginationPromises.push(
          agent.fillButton(4, 0, 0, 0).then(() => ({ index: 4 })),
          agent.fillButton(9, 0, 0, 0).then(() => ({ index: 9 })),
        );
      }

      const holdPromise = isHeld
        ? renderActionButton('Release', '#d97706', '#fff').then((image) => ({
            index: 8,
            image,
            onPress: () => onRelease(),
          }))
        : renderActionButton('Hold', '#374151', '#fff').then((image) => ({
            index: 8,
            image,
            onPress: () => onHold(),
          }));

      const finishPromise = renderFinishButton(
        ticketStatus,
        btn13.width,
        btn13.height,
      ).then((image) => ({
        index: 13,
        image,
        onPress: () => onFinish(),
      }));

      Promise.all([
        clearEmpty(),
        Promise.all([
          ...materialImagePromises,
          deductionPromise,
          ...paginationPromises,
          holdPromise,
          finishPromise,
        ]).then((allConfigs) => agent.configureButtons(allConfigs)),
      ]).catch((err) => console.warn('[UStudio] button render failed', err));

      return;
    }

    if (screen === 'LineItemEdit') {
      const digitPromises = Object.entries(DIGIT_MAP).map(
        async ([btnIdx, digit]) => {
          const image = await renderDigitButton(digit);
          return {
            index: Number(btnIdx),
            image,
            onPress: () => {
              if (!currentLineItemId || !focusedFieldRef.current) return;
              centsRef.current = centsRef.current * 10 + Number(digit);
              const display = (centsRef.current / 100).toFixed(2);
              onSetFieldValue(
                currentLineItemId,
                focusedFieldRef.current,
                display,
              );
            },
          } satisfies ButtonConfig;
        },
      );

      const zeroPromise = renderDigitButton('0').then((image) => ({
        index: 4,
        image,
        onPress: () => {
          if (!currentLineItemId || !focusedFieldRef.current) return;
          centsRef.current = centsRef.current * 10;
          const display = (centsRef.current / 100).toFixed(2);
          onSetFieldValue(currentLineItemId, focusedFieldRef.current, display);
        },
      }));

      const pricePromise = renderActionButton('P', '#1e40af', '#fff').then(
        (image) => ({
          index: 3,
          image,
          onPress: () => {
            if (!currentLineItemId) return;
            focusedFieldRef.current = 'unitPrice';
            centsRef.current = 0;
            onFocusField(currentLineItemId, 'unitPrice');
          },
        }),
      );

      const unitPromise = renderActionButton('U', '#374151', '#fff').then(
        (image) => ({
          index: 8,
          image,
          onPress: () => setScreen('UnitSelect'),
        }),
      );

      const gtLabel = weightField === 'gross' ? 'G' : 'T';
      const gtPromise = renderActionButton(gtLabel, '#374151', '#fff').then(
        (image) => ({
          index: 9,
          image,
          onPress: () => {
            if (!currentLineItemId) return;
            const nextField = weightField === 'gross' ? 'tare' : 'gross';
            setWeightField(nextField);
            focusedFieldRef.current = nextField;
            centsRef.current = 0;
            onFocusField(currentLineItemId, nextField);
            onTakeWeight(currentLineItemId, nextField);
          },
        }),
      );

      const finishPromise = renderFinishButton(
        ticketStatus,
        btn13.width,
        btn13.height,
      ).then((image) => ({
        index: 13,
        image,
        onPress: () => onFinish(),
      }));

      Promise.all([
        ...digitPromises,
        zeroPromise,
        pricePromise,
        unitPromise,
        gtPromise,
        finishPromise,
      ])
        .then((allConfigs) => agent.configureButtons(allConfigs))
        .catch((err) => console.warn('[UStudio] button render failed', err));

      return;
    }

    if (screen === 'UnitSelect') {
      const unitPromises = availableUnits
        .slice(0, MATERIALS_PER_PAGE)
        .map(async (unit, idx) => {
          const btnIndex = MATERIAL_SLOTS[idx];
          const image = await renderActionButton(unit.label, '#1e3a5f', '#fff');
          return {
            index: btnIndex,
            image,
            onPress: () => {
              if (!currentLineItemId) return;
              onSetUnit(currentLineItemId, unit.value);
              setScreen('LineItemEdit');
            },
          } satisfies ButtonConfig;
        });

      const emptySlots = MATERIAL_SLOTS.slice(availableUnits.length);
      const clearEmpty = async () => {
        for (const btnIndex of emptySlots) {
          await agent.fillButton(btnIndex, 0, 0, 0);
        }
      };

      const pricePromise = renderActionButton('P', '#1e40af', '#fff').then(
        (image) => ({
          index: 3,
          image,
          onPress: () => {
            if (!currentLineItemId) return;
            focusedFieldRef.current = 'unitPrice';
            centsRef.current = 0;
            onFocusField(currentLineItemId, 'unitPrice');
            setScreen('LineItemEdit');
          },
        }),
      );

      const zeroPromise = renderDigitButton('0').then((image) => ({
        index: 4,
        image,
        onPress: () => {
          if (!currentLineItemId || !focusedFieldRef.current) return;
          centsRef.current = centsRef.current * 10;
          const display = (centsRef.current / 100).toFixed(2);
          onSetFieldValue(currentLineItemId, focusedFieldRef.current, display);
        },
      }));

      const newLinePromise = renderActionButton('N', '#16a34a', '#fff').then(
        (image) => ({
          index: 8,
          image,
          onPress: () => {
            setScreen('MaterialSelect');
          },
        }),
      );

      const gtLabel = weightField === 'gross' ? 'G' : 'T';
      const gtPromise = renderActionButton(gtLabel, '#374151', '#fff').then(
        (image) => ({
          index: 9,
          image,
          onPress: () => {
            if (!currentLineItemId) return;
            const nextField = weightField === 'gross' ? 'tare' : 'gross';
            setWeightField(nextField);
            focusedFieldRef.current = nextField;
            centsRef.current = 0;
            onFocusField(currentLineItemId, nextField);
            onTakeWeight(currentLineItemId, nextField);
            setScreen('LineItemEdit');
          },
        }),
      );

      const finishPromise = renderFinishButton(
        ticketStatus,
        btn13.width,
        btn13.height,
      ).then((image) => ({
        index: 13,
        image,
        onPress: () => onFinish(),
      }));

      Promise.all([
        clearEmpty(),
        Promise.all([
          ...unitPromises,
          pricePromise,
          zeroPromise,
          newLinePromise,
          gtPromise,
          finishPromise,
        ]).then((allConfigs) => agent.configureButtons(allConfigs)),
      ]).catch((err) => console.warn('[UStudio] button render failed', err));
    }
  }, [
    agent,
    screen,
    materialPage,
    quickAddItems,
    availableUnits,
    ticketStatus,
    isHeld,
    weightField,
    currentLineItemId,
    onAddItem,
    onAddDeduction,
    onFocusField,
    onSetFieldValue,
    onSetUnit,
    onHold,
    onRelease,
    onFinish,
    onTakeWeight,
  ]);

  React.useEffect(() => {
    if (!agent) return;
    return () => {
      restoreInitialLayout(agent).catch(() => undefined);
    };
  }, [agent]);

  return null;
};
